import { addMonths, format } from 'date-fns';
import { createDateSafe } from './dates';
import type { VacationBalance } from './vacation.types';
import type { DisplayBalance } from './vacationBalanceDisplay';
import { ENTITLED_DAYS_PER_PERIOD } from './vacationRules';

export const MONTHS_TO_ENTITLEMENT = 12;

export const MONTHS_TO_REQUEST = 9;

export type VacationStage = 'unknown' | 'ineligible' | 'tooEarly' | 'earlyRequest' | 'entitled';

export interface VacationAccess {
    stage: VacationStage;
    canUseForm: boolean;
    monthsWorked: number;
    earliestStartDate: string | null;
    canRequestFrom: string | null;
}

function toIso(date: Date): string {
    return format(date, 'yyyy-MM-dd');
}

export function resolveVacationAccess(balance: VacationBalance | null): VacationAccess {
    if (!balance) {
        return {
            stage: 'unknown',
            canUseForm: false,
            monthsWorked: 0,
            earliestStartDate: null,
            canRequestFrom: null,
        };
    }

    if (!balance.eligible) {
        return {
            stage: 'ineligible',
            canUseForm: false,
            monthsWorked: balance.monthsWorked,
            earliestStartDate: null,
            canRequestFrom: null,
        };
    }

    const admission = createDateSafe(balance.admissionDate);
    const canRequestFrom = admission ? toIso(addMonths(admission, MONTHS_TO_REQUEST)) : null;

    if (balance.monthsWorked < MONTHS_TO_REQUEST) {
        return {
            stage: 'tooEarly',
            canUseForm: false,
            monthsWorked: balance.monthsWorked,
            earliestStartDate: null,
            canRequestFrom,
        };
    }

    if (balance.monthsWorked < MONTHS_TO_ENTITLEMENT) {
        return {
            stage: 'earlyRequest',
            canUseForm: true,
            monthsWorked: balance.monthsWorked,
            earliestStartDate: balance.entitledFrom,
            canRequestFrom,
        };
    }

    return {
        stage: 'entitled',
        canUseForm: true,
        monthsWorked: balance.monthsWorked,
        earliestStartDate: null,
        canRequestFrom,
    };
}

export function buildFormBalance(
    balance: VacationBalance | null,
    displayBalance: DisplayBalance,
    access: VacationAccess
): VacationBalance | null {
    if (!balance) return null;

    const isEarlyRequest = access.stage === 'earlyRequest';

    return {
        ...balance,
        balanceDays:
            isEarlyRequest ? ENTITLED_DAYS_PER_PERIOD : displayBalance.balanceDays,
        availableDays:
            isEarlyRequest ? ENTITLED_DAYS_PER_PERIOD : displayBalance.availableDays,
        scheduledDays: displayBalance.scheduledDays,
    };
}
